import * as React from "react";
import {inject, observer} from "mobx-react";
import {observable} from "mobx";
import type {IActionBarItem} from "../abstract/ModuleView";
import {ModuleView} from "../abstract/ModuleView";
import {XyiconFeature, Permission} from "../../../generated/api/base";
import type {AppState} from "../../../data/state/AppState";
import type {Space} from "../../../data/models/Space";
import type {IModel} from "../../../data/models/Model";
import type {SpaceViewRenderer} from "./spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {CreateSpacePanel} from "./create/CreateSpacePanel";

export type OverlayPanel = "Layer" | "ConditionalFormatting" | "Caption";

export interface IBasicPropsFor3DTweaking {
	readonly spaceViewRenderer?: SpaceViewRenderer;
	readonly appState?: AppState;
}

interface ISpaceViewProps {
	readonly appState?: AppState;
}

@inject("appState")
@observer
export class SpaceView extends React.Component<ISpaceViewProps> {
	@observable
	private _appState: AppState;

	constructor(props: ISpaceViewProps) {
		super(props);
		this._appState = props.appState;
	}

	public override componentDidUpdate(prevProps: ISpaceViewProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	private hasAnyTypePermission = () => {
		return this._appState.actions.hasAnyTypeTheGivenPermissionInModule(XyiconFeature.Space, Permission.Update);
	};

	private hasEveryTypePermission(items: Space[], givenPermission: Permission) {
		if (!this._appState.user) {
			return false;
		}
		if (this._appState.user.isAdmin) {
			return true;
		}

		return items.every((item) => {
			const permission = this._appState.actions.getModuleTypePermission(item.typeId, XyiconFeature.Space);

			return permission >= givenPermission;
		});
	}

	private isDeleteEnabled = (selectedItems: Space[]) => {
		if (selectedItems.length === 0) {
			return false;
		}

		return this.hasEveryTypePermission(selectedItems, Permission.Delete);
	};

	public override render() {
		const actionBarItems: IActionBarItem<IModel>[] = [
			{
				id: "import",
				title: "Update Spaces with an Excel file",
				label: "Import",
			},
			{
				id: "add",
				title: "Create Space",
				label: "Create",
				enabled: this.hasAnyTypePermission,
			},
			{
				id: "delete",
				title: "Delete Space",
				label: "Delete",
				enabled: this.isDeleteEnabled,
			},
			{
				id: "manageVersionSets",
				title: "Manage Version Sets",
				label: "Manage Versions",
				enabled: this.hasAnyTypePermission,
				icon: "manage-versions",
			},
		];

		return (
			<ModuleView
				feature={XyiconFeature.Space}
				className="SpaceView"
				actionBar={actionBarItems}
				create={(onClose) => <CreateSpacePanel onClose={onClose} />}
			/>
		);
	}
}
